import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import Icon from "../../components/Icon";
import {
  RESPONSE_PAGE_SIZE,
  SURVEY_ACTIONS,
  defaultRange,
  formatDateTime,
  formatScore,
  useSurveyRequest
} from "./api";
import { choiceBars, histogramBars, isNpsSummary, scaleBars } from "./summary";
import "./style.scss";

const DEFAULT_MONTHS_BACK = 3;

// A five-point star, filled up to the score, for a star-scale survey (CSAT).
const STAR_PATH =
  "M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z";

const Stars = ({ value, max }) => (
  <span className="survey-stars">
    {Array.from({ length: max }, (_, index) => (
      <svg
        key={index}
        className={index < value ? "survey-star on" : "survey-star"}
        width="15"
        height="15"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d={STAR_PATH} />
      </svg>
    ))}
  </span>
);

/*
 * "Use CSAT to monitor the application experience", and the quarterly NPS behind
 * "Customer Loyalty Growth: track and measure loyalty trends over time".
 *
 * The page answers two questions and keeps them apart on purpose. The cards and the histogram
 * say what the score is. The table underneath says why: a CSAT of 3.4 is not something anyone
 * can act on, but the six sentences customers typed next to it are.
 *
 * Nothing here can change a survey. The questions live in be-notification's config so that the
 * app renders whatever it is handed, and a question edited from this screen would silently
 * change the meaning of every score already recorded against it.
 */
function SurveyInsight() {
  const requestSurvey = useSurveyRequest();

  const [surveys, SETxSurveys] = useState([]);
  const [activeCode, SETxActiveCode] = useState("");
  const [range, SETxRange] = useState(() => defaultRange(DEFAULT_MONTHS_BACK));
  const [appliedRange, SETxAppliedRange] = useState(() => defaultRange(DEFAULT_MONTHS_BACK));
  const [summary, SETxSummary] = useState(null);
  const [responses, SETxResponses] = useState([]);
  const [pagination, SETxPagination] = useState(null);
  const [page, SETxPage] = useState(1);
  const [loading, SETxLoading] = useState(false);
  const [error, SETxError] = useState(null);

  // A reply that arrives after the operator has moved on is dropped rather than written over
  // newer state - switching tabs quickly used to leave one survey's score above another's rows.
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const describeError = requestError =>
    requestError && requestError.status === 404
      ? "Survey not found in be-notification"
      : (requestError && requestError.message) || "Failed to load data";

  /* Which surveys exist. Served, not hard-coded, so a new survey needs no dashboard release. */
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const result = await requestSurvey(SURVEY_ACTIONS.list);
        if (!live || result.signedOut) {
          return;
        }
        const list = (result.data && result.data.data) || [];
        SETxSurveys(list);
        SETxActiveCode(current => current || (list[0] && list[0].code) || "");
      } catch (requestError) {
        if (live) {
          SETxError(describeError(requestError));
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [requestSurvey]);

  const fetchSurvey = useCallback(
    async (code, activeRange, activePage) => {
      if (!code) {
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;

      SETxLoading(true);
      SETxError(null);
      try {
        const params = { from: activeRange.from, to: activeRange.to };
        const [summaryResult, responseResult] = await Promise.all([
          requestSurvey(SURVEY_ACTIONS.summary, { code, query: params }),
          requestSurvey(SURVEY_ACTIONS.responses, {
            code,
            query: { ...params, page: activePage, limit: RESPONSE_PAGE_SIZE }
          })
        ]);

        if (!isCurrent() || summaryResult.signedOut || responseResult.signedOut) {
          return;
        }
        SETxSummary(summaryResult.data);
        SETxResponses((responseResult.data && responseResult.data.data) || []);
        SETxPagination((responseResult.data && responseResult.data.pagination) || null);
      } catch (requestError) {
        if (!isCurrent()) {
          return;
        }
        SETxSummary(null);
        SETxResponses([]);
        SETxPagination(null);
        SETxError(describeError(requestError));
      } finally {
        if (isCurrent()) {
          SETxLoading(false);
        }
      }
    },
    [requestSurvey]
  );

  useEffect(() => {
    fetchSurvey(activeCode, appliedRange, page);
  }, [fetchSurvey, activeCode, appliedRange, page]);

  const activeSurvey = useMemo(
    () => surveys.find(survey => survey.code === activeCode) || null,
    [surveys, activeCode]
  );

  const bars = useMemo(() => scaleBars(activeSurvey, summary), [activeSurvey, summary]);

  // The scored question's scale decides how a response renders: a 0..1 question is Yes / No,
  // anything wider is drawn as stars filled up to the score.
  const scoreScale = useMemo(() => {
    if (!activeSurvey) {
      return null;
    }
    const scored = (activeSurvey.questions || []).find(
      question => question.id === activeSurvey.scoreQuestionId
    );
    return (scored && scored.scale) || null;
  }, [activeSurvey]);
  const isBinary = Boolean(scoreScale && scoreScale.min === 0 && scoreScale.max === 1);

  // Green for a good score, amber in the middle, red for a poor one — judged against the scale.
  const scoreTone = score => {
    if (isBinary) {
      return score >= 1 ? "good" : "bad";
    }
    const max = (scoreScale && scoreScale.max) || 5;
    const ratio = max > 0 ? score / max : 0;
    if (ratio >= 0.8) {
      return "good";
    }
    return ratio >= 0.5 ? "mid" : "bad";
  };

  // The per-question breakdowns behind the headline score: the CES scale and the
  // "what to improve" choice distribution (served since requirement sheet 5B).
  const questionSummaries = useMemo(() => (summary && summary.questionSummaries) || [], [summary]);

  const isNps = isNpsSummary(summary);
  const totalPages = (pagination && pagination.totalPages) || 0;

  const onApply = event => {
    event.preventDefault();
    SETxPage(1);
    SETxAppliedRange({ ...range });
  };

  const onReset = () => {
    const fresh = defaultRange(DEFAULT_MONTHS_BACK);
    SETxRange(fresh);
    SETxPage(1);
    SETxAppliedRange(fresh);
  };

  return (
    <Fragment>
      {loading ? (
        <div className="survey-progress">
          <CircularProgress />
          <p className="survey-progress-text">Loading survey results...</p>
        </div>
      ) : null}

      <div className="survey-container">
        <div className="survey-topbar">
          <div className="survey-titles">
            <h2 className="survey-heading">CSAT / NPS</h2>
            <p className="survey-subheading">
              Survey results customers submit from the Principal TH app — scores, trends, and individual comments
            </p>
          </div>
          {/*
            * The survey itself goes out as a notification, so "ask more customers" is the
            * compose form rather than anything on this page.
            */}
          <Link className="survey-send" to="/PushNoti_Compose">
            <Icon name="send" size={16} />
            Send survey
          </Link>
        </div>

        <div className="survey-tabs">
          {surveys.map(survey => (
            <button
              type="button"
              key={survey.code}
              className={survey.code === activeCode ? "survey-tab active" : "survey-tab"}
              onClick={() => {
                SETxPage(1);
                SETxActiveCode(survey.code);
              }}
            >
              {survey.titleTh || survey.code}
            </button>
          ))}
        </div>

        {activeSurvey ? (
          <p className="survey-code-line">
            <span className="survey-code">{activeSurvey.code}</span>
            Repeats every {activeSurvey.cooldownDays} days ·{" "}
            {(activeSurvey.questions || []).length}{" "}
            {(activeSurvey.questions || []).length === 1 ? "question" : "questions"}
          </p>
        ) : null}

        <form className="survey-filter" onSubmit={onApply}>
          <div className="survey-filter-field">
            <span className="survey-filter-label">From date</span>
            <input
              type="date"
              value={range.from}
              onChange={event => SETxRange({ ...range, from: event.target.value })}
            />
          </div>
          <div className="survey-filter-field">
            <span className="survey-filter-label">To date</span>
            <input
              type="date"
              value={range.to}
              onChange={event => SETxRange({ ...range, to: event.target.value })}
            />
          </div>
          <div className="survey-filter-actions">
            <button type="submit" className="survey-apply">
              <Icon name="filter" size={15} />
              View
            </button>
            <button type="button" className="survey-reset" onClick={onReset}>
              <Icon name="refresh" size={15} />
              Clear dates
            </button>
          </div>
        </form>

        {error ? <div className="survey-error">{error}</div> : null}

        <div className="survey-cards">
          <div className="survey-card">
            <span className="survey-card-label">Responses</span>
            <span className="survey-card-value">{(summary && summary.total) || 0}</span>
          </div>
          <div className="survey-card">
            <span className="survey-card-label">Average score</span>
            <span className="survey-card-value">
              {formatScore(summary && summary.averageScore, summary && summary.total)}
            </span>
          </div>
          {isNps ? (
            <Fragment>
              <div className="survey-card accent">
                <span className="survey-card-label">NPS</span>
                <span className="survey-card-value">{summary.nps}</span>
                <span className="survey-card-note">promoter − detractor</span>
              </div>
              <div className="survey-card">
                <span className="survey-card-label">Promoter (9-10)</span>
                <span className="survey-card-value">{summary.promoters}</span>
              </div>
              <div className="survey-card">
                <span className="survey-card-label">Passive (7-8)</span>
                <span className="survey-card-value">{summary.passives}</span>
              </div>
              <div className="survey-card">
                <span className="survey-card-label">Detractor (0-6)</span>
                <span className="survey-card-value">{summary.detractors}</span>
              </div>
            </Fragment>
          ) : null}
        </div>

        {bars.length > 0 ? (
          <div className="survey-histogram">
            <p className="survey-section-title">Score distribution</p>
            {bars.map(bar => (
              <div className="survey-bar-row" key={bar.score}>
                <span className="survey-bar-score">{bar.score}</span>
                <span className="survey-bar-track">
                  <span className="survey-bar-fill" style={{ width: `${bar.percent}%` }} />
                </span>
                <span className="survey-bar-count">{bar.count}</span>
              </div>
            ))}
          </div>
        ) : null}

        {questionSummaries.map(question => (
          <div className="survey-breakdown" key={question.id}>
            <p className="survey-section-title">{question.titleEn || question.titleTh}</p>
            {question.type === "SCALE" ? (
              <Fragment>
                <p className="survey-breakdown-meta">
                  Average {formatScore(question.averageScore, question.answered)} · {question.answered} answers
                </p>
                <div className="survey-histogram">
                  {histogramBars(
                    question.histogram,
                    question.scale && question.scale.min,
                    question.scale && question.scale.max
                  ).map(bar => (
                    <div className="survey-bar-row" key={bar.score}>
                      <span className="survey-bar-score">{bar.score}</span>
                      <span className="survey-bar-track">
                        <span className="survey-bar-fill" style={{ width: `${bar.percent}%` }} />
                      </span>
                      <span className="survey-bar-count">{bar.count}</span>
                    </div>
                  ))}
                </div>
              </Fragment>
            ) : (
              <div className="survey-histogram">
                {choiceBars(question.choices).map(bar => (
                  <div className="survey-bar-row" key={bar.value}>
                    <span className="survey-bar-label">{bar.label}</span>
                    <span className="survey-bar-track">
                      <span className="survey-bar-fill" style={{ width: `${bar.percent}%` }} />
                    </span>
                    <span className="survey-bar-count">{bar.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <p className="survey-section-title">Individual responses</p>

        <div className="survey-table-wrap">
          <table className="survey-table">
            <thead>
              <tr>
                <th className="survey-col-submitted">Submitted</th>
                <th className="survey-col-score">Score</th>
                <th className="survey-col-uuid">Customer uuid</th>
                <th className="survey-col-group">Customer group · app</th>
              </tr>
            </thead>
            <tbody>
              {responses.map(response => (
                <tr key={response.id}>
                  <td>{formatDateTime(response.createdAt)}</td>
                  <td>
                    <div className="survey-score-cell">
                      <span className={`survey-score-badge ${scoreTone(response.score)}`}>
                        {response.score}
                      </span>
                      {isBinary ? (
                        <span className="survey-yesno">{response.score >= 1 ? "Yes" : "No"}</span>
                      ) : scoreScale ? (
                        <Stars value={response.score} max={scoreScale.max} />
                      ) : null}
                    </div>
                  </td>
                  <td className="survey-cell-uuid">{response.customerUuid || "-"}</td>
                  <td>
                    {response.channel || "-"}
                    {response.appVersion ? ` · v${response.appVersion}` : ""}
                  </td>
                </tr>
              ))}

              {!loading && responses.length === 0 ? (
                <tr>
                  <td className="survey-empty" colSpan={4}>
                    No responses in this date range
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total > 0 ? (
          <div className="survey-pager">
            <span className="survey-pager-count">
              {(pagination.page - 1) * (pagination.limit || RESPONSE_PAGE_SIZE) + 1}–
              {(pagination.page - 1) * (pagination.limit || RESPONSE_PAGE_SIZE) + responses.length} of{" "}
              {pagination.total}
            </span>
            <div className="survey-pager-nav">
              <button type="button" disabled={page <= 1} onClick={() => SETxPage(page - 1)}>
                ‹ Prev
              </button>
              <span className="survey-pager-page">
                {pagination.page} / {totalPages || 1}
              </span>
              <button
                type="button"
                disabled={totalPages ? page >= totalPages : true}
                onClick={() => SETxPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Fragment>
  );
}

export default SurveyInsight;
